import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  CardActionArea,
  Chip,
} from '@mui/material';

import api from '../../utils/api';

// Import our component styles hook
import useComponentStyles from '../../theme/useComponentStyles';

// Import phosphor icons for consistency
import { 
  Envelope, 
  CalendarBlank, 
  Printer
} from '@phosphor-icons/react';

function StationaryGifts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weddingDetails, setWeddingDetails] = useState(null);
  const [_wedding, setWedding] = useState(null);
  const styles = useComponentStyles();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [weddingRes, detailsRes] = await Promise.all([
          api.get('/api/wedding'),
          api.get('/api/wedding-details')
        ]);
        
        setWedding(weddingRes.data);
        setWeddingDetails(detailsRes.data);
      } catch (error) {
        setError('Failed to fetch wedding information');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const _getWeddingDate = () => {
    if (!weddingDetails?.dates?.ceremonyDate) return 'Not set';
    
    try {
      return new Date(weddingDetails.dates.ceremonyDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const menuOptions = [
    {
      title: 'Make Invitations',
      description: 'Create beautiful wedding invitations',
      icon: <Envelope size={32} weight="regular" color="#E91E63" />,
      path: '/stationary/invitations',
      bgColor: 'rgba(233, 30, 99, 0.1)',
      status: 'Ready to create',
    },
    {
      title: 'Order of the Day',
      description: 'Design your wedding day schedule cards',
      icon: <CalendarBlank size={32} weight="regular" color="#9C27B0" />,
      path: '/stationary/order-of-day',
      bgColor: 'rgba(156, 39, 176, 0.1)',
      status: 'Coming soon',
    },
    {
      title: 'Place Cards',
      description: 'Create personalized seating cards',
      icon: <Printer size={32} weight="regular" color="#3F51B5" />,
      path: '/stationary/place-cards',
      bgColor: 'rgba(63, 81, 181, 0.1)',
      status: 'Coming soon',
    },
    {
      title: 'Thank You Cards',
      description: 'Design post-wedding thank you notes',
      icon: <Envelope size={32} weight="regular" color="#2196F3" />,
      path: '/stationary/thank-you',
      bgColor: 'rgba(33, 150, 243, 0.1)',
      status: 'Coming soon',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.containers.pageContainer}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            My Stationary
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={styles.text.sectionDescription}>
            Create beautiful invitations, cards, and other wedding stationary.
          </Typography>
        </Grid>

        {/* Menu options grid */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {menuOptions.map((option, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{
                    ...styles.cards.dashboardCard,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardActionArea 
                    onClick={() => navigate(option.path)}
                    sx={{ height: '100%', p: 3 }}
                    disabled={option.status === 'Coming soon'}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        textAlign: 'center',
                        height: '100%'
                      }}>
                        <Box sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          display: 'flex',
                          lineHeight: 0,
                          '& svg': {
                            display: 'block'
                          },
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          backgroundColor: option.bgColor,
                        }}>
                          {option.icon}
                        </Box>
                        
                        <Typography variant="h6" gutterBottom sx={styles.text.cardTitle}>
                          {option.title}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {option.description}
                        </Typography>
                        
                        <Chip 
                          label={option.status}
                          size="small"
                          color={option.status === 'Ready to create' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default StationaryGifts; 